import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ConfirmModal } from '@/card/record/ConfirmModal'
import { MoreMenu } from '@/card/record/RecordHeader'
import { StateView } from '@/card/record/StateView'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatNumber } from '@/lib/format'
import { createReport, deleteReport, exportReport, fetchReports, fetchRuns, fetchSources, previewReport, runReport, updateReport } from './api'
import { emptyDefinition, operatorsFor, REPORT_CATEGORIES, takesValue, withOperator } from './types'
import { reportColumnHeader, reportExportHeaders } from './headers'
import type {
  Aggregate,
  ReportCategory,
  ReportDefinition,
  ReportField,
  ReportFilter,
  ReportResult,
  ReportRun,
  ReportSource,
  SavedReport,
} from './types'
import '@/styles/work-analytics.css'

/**
 * Конструктор отчётов (ReportBuilder.dc.html; ТЗ §4.6, тикет #76).
 *
 * <h2>Критерий тикета</h2>
 *
 * Руководитель строит отчёт по своим данным без разработчика: выбирает витрину, измерения,
 * меры и фильтры, смотрит предпросмотр, сохраняет. Разработчик нужен только чтобы добавить
 * витрину в реестр — это решает, какие данные вообще доступны отчётности.
 *
 * <h2>Откуда берётся список полей</h2>
 *
 * Целиком с сервера (`ReportCatalog`). Форма не может предложить колонку, которой в витрине
 * нет, — но это удобство, а не защита: белый список проверяется на сервере.
 *
 * <h2>Псевдоним меры собирается из поля и агрегата</h2>
 *
 * Псевдоним — единственная пользовательская строка, попадающая в SQL идентификатором, и у
 * сервера на него отдельная проверка регулярным выражением. Поля для него нет: собранный из
 * поля и агрегата проходит проверку всегда.
 *
 * <h2>Отказ по источнику — словами</h2>
 *
 * `report.forbidden_source` показывается состоянием «Нет доступа» на месте предпросмотра, а
 * не пустой таблицей: пустота читается как «данных нет», и запрет остался бы невидимым.
 *
 * <h2>Почему предпросмотр по кнопке</h2>
 *
 * Каждое изменение — запрос к витрине; автообновление превратило бы сборку отчёта в очередь
 * запросов, из которых нужен последний. Нажатие делает момент запроса явным.
 *
 * <h2>Чего нет из макета</h2>
 *
 * Перетаскивания полей (поле добавляется щелчком или Enter), медианы, доли от итога и
 * количества уникальных (сервер знает только sum/avg/min/max/count), подытогов и раскрытия
 * групп, автора и «Просмотр: …» (личных отчётов с доступом по людям в API нет), дублирования.
 */
export function ReportBuilderPage() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()

  const sourcesQuery = useQuery({
    queryKey: ['analytics-sources'],
    queryFn: ({ signal }) => fetchSources(signal),
    staleTime: 30 * 60 * 1000,
    retry: false,
  })
  const savedQuery = useQuery({
    queryKey: ['analytics-reports'],
    queryFn: ({ signal }) => fetchReports(signal),
    retry: false,
  })

  const sources = useMemo(() => sourcesQuery.data ?? [], [sourcesQuery.data])
  const [definition, setDefinition] = useState<ReportDefinition | null>(null)
  const [preview, setPreview] = useState<ReportResult | null>(null)
  const [previewError, setPreviewError] = useState<unknown>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null)
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [category, setCategory] = useState<ReportCategory>('sales')
  const [loaded, setLoaded] = useState<SavedReport | null>(null)
  const [deleting, setDeleting] = useState<SavedReport | null>(null)

  const source = sources.find((candidate) => candidate.code === definition?.source) ?? null
  const mayCreate = can(user, 'analytics.report', 'create')
  const mayDelete = can(user, 'analytics.report', 'delete')
  const mayUpdate = can(user, 'analytics.report', 'update')
  const mayExport = can(user, 'analytics.report', 'export')

  const failed = (error: unknown) => setNotice({ kind: 'danger', text: describe(error, t) })

  const previewMutation = useMutation({
    mutationFn: (draft: ReportDefinition) => previewReport(draft, 50),
    onSuccess: (result) => {
      setPreview(result)
      setPreviewError(null)
    },
    onError: (error: unknown) => {
      setPreview(null)
      setPreviewError(error)
    },
  })

  // #94: a report opened from the saved list is changed in place (PUT), not copied — a copy
  // under the same code would be refused, and under a new code it would leave the old one
  // behind for the dashboards that still read it. "Save as new" stays a separate action.
  const editable = loaded !== null && !loaded.isSystem && mayUpdate
  const saveMutation = useMutation({
    mutationFn: ({ draft, asNew }: { draft: ReportDefinition; asNew: boolean }) =>
      !asNew && editable && loaded
        ? updateReport(loaded.id, { code, name, category, definition: draft, useReplica: loaded.useReplica })
        : createReport({ code, name, category, definition: draft }),
    onSuccess: (saved) => {
      setNotice({ kind: 'success', text: t('report.saved', { name: saved.name }) })
      setLoaded(saved)
      void queryClient.invalidateQueries({ queryKey: ['analytics-reports'] })
    },
    onError: failed,
  })

  const runMutation = useMutation({
    mutationFn: (report: SavedReport) => runReport(report.id),
    onMutate: (report) => {
      // Отчёт открывается в конструкторе: видно, что именно он считает.
      setLoaded(report)
      setDefinition(report.definition)
      setName(report.name)
      setCode(report.code)
      setCategory(report.category)
      setPreview(null)
      setPreviewError(null)
    },
    onSuccess: (result, report) => {
      setPreview(result)
      void queryClient.invalidateQueries({ queryKey: ['report-runs', report.id] })
    },
    // Отказ по правам — состоянием «нет доступа», а не пустой таблицей.
    onError: (error: unknown, report) => {
      setPreviewError(error)
      void queryClient.invalidateQueries({ queryKey: ['report-runs', report.id] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (reportId: string) => deleteReport(reportId),
    onSuccess: () => {
      setDeleting(null)
      if (loaded && deleting && loaded.id === deleting.id) setLoaded(null)
      void queryClient.invalidateQueries({ queryKey: ['analytics-reports'] })
    },
    onError: (error: unknown) => {
      setDeleting(null)
      failed(error)
    },
  })

  const crumbs = (
    <nav className="crumbs" aria-label={t('record.crumbs')}>
      <span>{t('nav.section.analytics')}</span>
      <span aria-hidden="true">/</span>
      <span aria-current="page">{t('rb.crumb')}</span>
    </nav>
  )

  if (sourcesQuery.isLoading) {
    return (
      <div className="stack" style={{ gap: 14 }} aria-busy="true" aria-label={t('rb.loading')}>
        {crumbs}
        <div className="builder">
          <div className="panel panel-b stack" style={{ gap: 12 }}>
            {Array.from({ length: 10 }, (_, index) => (
              <span key={index} className="skel" style={{ width: `${45 + ((index * 19) % 45)}%` }} />
            ))}
          </div>
          <div className="panel panel-b stack" style={{ gap: 8 }}>
            {Array.from({ length: 4 }, (_, index) => (
              <span key={index} className="skel" style={{ height: 40, borderRadius: 6 }} />
            ))}
          </div>
        </div>
      </div>
    )
  }
  if (sourcesQuery.error) {
    const forbidden = sourcesQuery.error instanceof ApiError && sourcesQuery.error.isForbidden
    return (
      <div className="stack" style={{ gap: 14 }}>
        {crumbs}
        <div className="page-head"><h1 className="h1">{t('nav.reportBuilder')}</h1></div>
        <StateView
          kind={forbidden ? 'forbidden' : 'error'}
          boxed
          title={forbidden ? t('rb.forbidden.title') : t('rb.error.title')}
          description={forbidden ? t('rb.forbidden.text') : describe(sourcesQuery.error, t)}
          actions={
            forbidden ? undefined : (
              <button className="btn btn-primary btn-sm" type="button" onClick={() => void sourcesQuery.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('state.retry')}
              </button>
            )
          }
        />
      </div>
    )
  }

  function chooseSource(next: string) {
    setDefinition(next ? emptyDefinition(next) : null)
    setPreview(null)
    setPreviewError(null)
    setLoaded(null)
  }
  function patch(change: Partial<ReportDefinition>) {
    setDefinition((current) => (current ? { ...current, ...change } : current))
  }
  function toggleDimension(field: string) {
    if (!definition) return
    patch({
      dimensions: definition.dimensions.includes(field)
        ? definition.dimensions.filter((item) => item !== field)
        : [...definition.dimensions, field],
    })
  }
  function addMeasure(field: string, aggregate: Aggregate) {
    if (!definition) return
    // Псевдоним из поля и агрегата: сервер проверяет его регулярным выражением, собранный
    // так проходит всегда, а ручной ввод мог бы не пройти.
    const alias = `${aggregate}_${field}`
    if (definition.measures.some((measure) => measure.alias === alias)) return
    patch({ measures: [...definition.measures, { field, aggregate, alias }] })
  }
  function changeAggregate(alias: string, aggregate: Aggregate) {
    if (!definition) return
    const measure = definition.measures.find((m) => m.alias === alias)
    if (!measure) return
    const nextAlias = `${aggregate}_${measure.field}`
    if (definition.measures.some((m) => m.alias === nextAlias)) return
    patch({
      measures: definition.measures.map((m) => (m.alias === alias ? { ...m, aggregate, alias: nextAlias } : m)),
      sort: definition.sort.map((s) => (s.field === alias ? { ...s, field: nextAlias } : s)),
    })
  }
  function removeMeasure(alias: string) {
    if (!definition) return
    patch({
      measures: definition.measures.filter((measure) => measure.alias !== alias),
      sort: definition.sort.filter((s) => s.field !== alias),
    })
  }
  function addFilter(field: ReportField) {
    if (!definition) return
    const operator = operatorsFor(field.type)[0]
    if (!operator) return
    patch({ filters: [...definition.filters, { field: field.name, operator, value: '' }] })
  }
  function updateFilter(index: number, change: Partial<ReportFilter>) {
    if (!definition) return
    patch({ filters: definition.filters.map((filter, at) => (at === index ? { ...filter, ...change } : filter)) })
  }
  function removeFilter(index: number) {
    if (!definition) return
    patch({ filters: definition.filters.filter((_, at) => at !== index) })
  }

  const canRunPreview = Boolean(definition && definition.measures.length > 0)
  const noSources = sources.length === 0

  return (
    <div className="stack" style={{ gap: 14 }}>
      {crumbs}

      <div className="rec-head">
        <div className="rec-title">
          <div className="row" style={{ gap: 6 }}>
            <input
              className="h1 wa-title-input"
              aria-label={t('report.name')}
              placeholder={t('rb.untitled')}
              value={name}
              readOnly={!mayCreate}
              onChange={(event) => setName(event.target.value)}
            />
            {mayCreate && <Icon name="settings" size="sm" className="muted" />}
          </div>
          <div className="rec-meta">
            {mayCreate ? (
              <>
                <label className="row" style={{ gap: 6 }}>
                  <span className="muted">{t('report.code')}</span>
                  <input
                    className="input input-sm mono"
                    aria-label={t('report.code')}
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    style={{ width: 180 }}
                  />
                </label>
                <span className="sep-dot" aria-hidden="true">·</span>
                <select
                  className="input input-sm select"
                  aria-label={t('report.category')}
                  value={category}
                  onChange={(event) => setCategory(event.target.value as ReportCategory)}
                  style={{ width: 'auto' }}
                >
                  {REPORT_CATEGORIES.map((option) => (
                    <option key={option} value={option}>{t(`report.category.${option}`)}</option>
                  ))}
                </select>
              </>
            ) : (
              <span>{t('rb.readOnly')}</span>
            )}
            {loaded && (
              <>
                <span className="sep-dot" aria-hidden="true">·</span>
                <span>{t('rb.savedAt', { at: new Date(loaded.updatedAt).toLocaleString() })}</span>
              </>
            )}
          </div>
        </div>
        <div className="rec-actions">
          {loaded && mayExport && (
            <>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => exportReport(loaded.id, loaded.code, 'xlsx', reportExportHeaders(t, loaded.definition)).catch(failed)}
              >
                <Icon name="download" />
                {t('rb.exportXlsx')}
              </button>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => exportReport(loaded.id, loaded.code, 'csv', reportExportHeaders(t, loaded.definition)).catch(failed)}
              >
                {t('rb.exportCsv')}
              </button>
            </>
          )}
          {editable && mayCreate && (
            <button
              className="btn btn-secondary"
              type="button"
              disabled={!definition || !canRunPreview || !code || !name || saveMutation.isPending}
              onClick={() => definition && saveMutation.mutate({ draft: definition, asNew: true })}
            >
              {t('rb.saveAsNew')}
            </button>
          )}
          {(editable || mayCreate) && (
            <button
              className="btn btn-primary"
              type="button"
              disabled={!definition || !canRunPreview || !code || !name || saveMutation.isPending}
              title={!canRunPreview ? t('rb.needMeasure') : !code || !name ? t('rb.needName') : undefined}
              onClick={() => definition && saveMutation.mutate({ draft: definition, asNew: false })}
            >
              {editable ? t('rb.saveChanges') : t('report.save')}
            </button>
          )}
          {loaded && mayDelete && !loaded.isSystem && (
            <MoreMenu
              label={t('rb.menu')}
              items={[{ id: 'delete', label: t('report.delete'), icon: 'trash', danger: true, onSelect: () => setDeleting(loaded) }]}
            />
          )}
        </div>
      </div>

      {notice && (
        <div className={`alert alert-${notice.kind}`} role="status">
          <Icon name={notice.kind === 'success' ? 'check' : 'alert'} />
          <span className="fg">{notice.text}</span>
        </div>
      )}

      <div className="builder">
        <aside className="stack" style={{ gap: 14 }}>
          <section className="panel" aria-labelledby="rb-src">
            <div className="panel-h">
              <Icon name="columns" className="muted" />
              <span className="h2" id="rb-src">{t('rb.source.title')}</span>
            </div>
            {noSources ? (
              // Ни одной доступной витрины — это запрет, а не пустой конструктор: форма,
              // которая ничего не построит, обещала бы экран, который всегда откажет.
              <StateView kind="forbidden" compact title={t('report.noSources')} description={t('rb.noSourcesText')} />
            ) : (
              <>
                <div className="panel-b stack" style={{ gap: 4 }}>
                  <label className="label" htmlFor="rb-source">{t('report.source')}</label>
                  <select
                    id="rb-source"
                    className="input select"
                    value={definition?.source ?? ''}
                    onChange={(event) => chooseSource(event.target.value)}
                  >
                    <option value="">{t('report.source.choose')}</option>
                    {sources.map((option) => (
                      <option key={option.code} value={option.code}>
                        {t(`report.source.${option.code}`)}
                      </option>
                    ))}
                  </select>
                  {source && <span className="help">{t('rb.source.help', { count: fieldCount(source) })}</span>}
                </div>
                {source && definition && (
                  <FieldList
                    source={source}
                    definition={definition}
                    onDimension={toggleDimension}
                    onMeasure={(field) => addMeasure(field, source.aggregates[0] ?? 'sum')}
                    onFilter={addFilter}
                  />
                )}
              </>
            )}
          </section>

          <SavedReports
            reports={savedQuery.data ?? []}
            isLoading={savedQuery.isLoading}
            loadedId={loaded?.id ?? null}
            mayExport={mayExport}
            mayDelete={mayDelete}
            onRun={(report) => runMutation.mutate(report)}
            onExport={(report) => exportReport(report.id, report.code, 'xlsx', reportExportHeaders(t, report.definition)).catch(failed)}
            onDelete={(report) => setDeleting(report)}
          />
          {loaded && <RunHistory reportId={loaded.id} />}
        </aside>

        <div className="stack" style={{ gap: 14, minWidth: 0 }}>
          {source && definition && (
            <section className="panel" aria-labelledby="rb-struct">
              <div className="panel-h">
                <span className="h2" id="rb-struct">{t('rb.structure')}</span>
                <span className="t-sm muted">{t('rb.structureHint')}</span>
                <span className="spacer" />
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => chooseSource(definition.source)}>
                  <Icon name="refresh" size="sm" />
                  {t('rb.reset')}
                </button>
              </div>
              <div className="panel-b stack" style={{ gap: 8 }}>
                <div className="well" role="group" aria-label={t('report.dimensions')}>
                  <span className="well-label">{t('report.dimensions')}</span>
                  {definition.dimensions.length === 0 && <span className="t-sm muted">{t('rb.dimensionsEmpty')}</span>}
                  {definition.dimensions.map((field) => (
                    <span key={field} className="fpill">
                      <span className="t">dim</span>
                      {t(`column.${field}`)}
                      <button type="button" aria-label={t('rb.removeDimension', { name: t(`column.${field}`) })} onClick={() => toggleDimension(field)}>
                        <Icon name="close" size="xs" />
                      </button>
                    </span>
                  ))}
                </div>

                <MeasureWell source={source} definition={definition} onAdd={addMeasure} onAggregate={changeAggregate} onRemove={removeMeasure} />

                <FilterWell source={source} definition={definition} onAdd={addFilter} onUpdate={updateFilter} onRemove={removeFilter} />

                <SortWell definition={definition} onChange={(sort) => patch({ sort })} />
              </div>
            </section>
          )}

          <section className="panel" aria-labelledby="rb-prev" style={{ overflow: 'hidden' }}>
            <div className="panel-h">
              <span className="h2" id="rb-prev">{t('rb.preview')}</span>
              {preview && (
                <span className="count">
                  {t('report.rows', { count: preview.rows.length })} · {t(`report.target.${preview.target}`)}
                </span>
              )}
              <span className="spacer" />
              {definition && (
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  disabled={!canRunPreview || previewMutation.isPending}
                  onClick={() => definition && previewMutation.mutate(definition)}
                >
                  <Icon name="refresh" size="sm" />
                  {t('report.preview')}
                </button>
              )}
            </div>
            <PreviewBody
              definition={definition}
              source={source}
              preview={preview}
              error={previewError}
              busy={previewMutation.isPending || runMutation.isPending}
              onAddMeasure={(field) => source && addMeasure(field, source.aggregates[0] ?? 'sum')}
              onRetry={() => definition && previewMutation.mutate(definition)}
            />
          </section>
        </div>
      </div>

      <ConfirmModal
        open={deleting != null}
        title={t('rb.deleteTitle', { name: deleting?.name ?? '' })}
        confirmLabel={t('report.delete')}
        pending={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
      >
        {t('rb.deleteText')}
      </ConfirmModal>
    </div>
  )
}

function fieldCount(source: ReportSource): number {
  return new Set([...source.dimensions, ...source.measures, ...source.filters].map((f) => f.name)).size
}

/**
 * Поля витрины (`.field-list`): щелчок или Enter ставит поле в подходящую строку по типу —
 * измерение в измерения, число в меры, остальное в фильтры.
 */
function FieldList({
  source,
  definition,
  onDimension,
  onMeasure,
  onFilter,
}: {
  source: ReportSource
  definition: ReportDefinition
  onDimension: (field: string) => void
  onMeasure: (field: string) => void
  onFilter: (field: ReportField) => void
}) {
  const { t } = useApp()
  const [query, setQuery] = useState('')
  const needle = query.trim().toLowerCase()
  const items: { kind: 'dim' | 'num' | 'flt'; field: ReportField; act: () => void; used: string | null }[] = [
    ...source.dimensions.map((field) => ({
      kind: 'dim' as const,
      field,
      act: () => onDimension(field.name),
      used: definition.dimensions.includes(field.name) ? t('rb.used.dimension') : null,
    })),
    ...source.measures.map((field) => ({
      kind: 'num' as const,
      field,
      act: () => onMeasure(field.name),
      used: definition.measures.some((m) => m.field === field.name) ? t('rb.used.measure') : null,
    })),
    ...source.filters.map((field) => ({
      kind: 'flt' as const,
      field,
      act: () => onFilter(field),
      used: definition.filters.some((f) => f.field === field.name) ? t('rb.used.filter') : null,
    })),
  ].filter((item) => !needle || t(`column.${item.field.name}`).toLowerCase().includes(needle) || item.field.name.includes(needle))

  return (
    <>
      <div className="list-sub">
        {t('rb.fields')}
        <span className="spacer" />
        <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>{fieldCount(source)}</span>
      </div>
      <div className="stack" style={{ gap: 6, padding: '8px 6px 10px' }}>
        <div className="input-icon" style={{ margin: '0 4px' }}>
          <Icon name="search" />
          <label htmlFor="rb-fq" className="sr-only">{t('rb.findField')}</label>
          <input id="rb-fq" className="input input-sm" style={{ paddingLeft: 30 }} placeholder={t('rb.findField')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="field-list" role="list" aria-label={t('rb.fields')}>
          {items.map((item) => (
            <div
              key={`${item.kind}-${item.field.name}`}
              className="fl-item"
              role="listitem"
              tabIndex={0}
              aria-label={t('rb.fieldAria', { name: t(`column.${item.field.name}`), kind: t(`rb.kind.${item.kind}`) })}
              onClick={item.act}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  item.act()
                }
              }}
            >
              <span className="t">{item.kind === 'flt' ? item.field.type.slice(0, 4) : item.kind}</span>
              <span className="ellipsis" style={{ flex: 1 }}>{t(`column.${item.field.name}`)}</span>
              {item.used && (
                <span className="row t-xs" style={{ gap: 3, color: 'var(--accent-text)' }}>
                  <Icon name="check" size="xs" />
                  {item.used}
                </span>
              )}
            </div>
          ))}
        </div>
        <span className="help" style={{ padding: '0 6px' }}>{t('rb.fieldsHelp')}</span>
      </div>
    </>
  )
}

/** Меры: колонка плюс агрегат; псевдоним не вводится. */
function MeasureWell({
  source,
  definition,
  onAdd,
  onAggregate,
  onRemove,
}: {
  source: ReportSource
  definition: ReportDefinition
  onAdd: (field: string, aggregate: Aggregate) => void
  onAggregate: (alias: string, aggregate: Aggregate) => void
  onRemove: (alias: string) => void
}) {
  const { t } = useApp()
  const [field, setField] = useState('')
  const [aggregate, setAggregate] = useState<Aggregate>(source.aggregates[0] ?? 'sum')
  return (
    <div className="well" role="group" aria-label={t('report.measures')}>
      <span className="well-label">{t('report.measures')}</span>
      {definition.measures.map((measure) => (
        <span key={measure.alias} className="fpill is-measure">
          <span className="t">num</span>
          {t(`column.${measure.field}`)}
          <select
            className="wa-pill-select"
            aria-label={t('rb.aggregateFor', { name: t(`column.${measure.field}`) })}
            value={measure.aggregate}
            onChange={(event) => onAggregate(measure.alias, event.target.value as Aggregate)}
          >
            {source.aggregates.map((option) => (
              <option key={option} value={option}>· {t(`report.aggregate.${option}`)}</option>
            ))}
          </select>
          <button type="button" aria-label={t('rb.removeMeasure', { name: t(`column.${measure.field}`) })} onClick={() => onRemove(measure.alias)}>
            <Icon name="close" size="xs" />
          </button>
        </span>
      ))}
      <span className="row" style={{ gap: 4 }}>
        <select className="input input-sm select" aria-label={t('report.measure.field')} value={field} onChange={(e) => setField(e.target.value)} style={{ width: 'auto' }}>
          <option value="">{t('report.measure.choose')}</option>
          {source.measures.map((measure) => (
            <option key={measure.name} value={measure.name}>{t(`column.${measure.name}`)}</option>
          ))}
        </select>
        <select
          className="input input-sm select"
          aria-label={t('report.measure.aggregate')}
          value={aggregate}
          onChange={(e) => setAggregate(e.target.value as Aggregate)}
          style={{ width: 'auto' }}
        >
          {source.aggregates.map((option) => (
            <option key={option} value={option}>{t(`report.aggregate.${option}`)}</option>
          ))}
        </select>
        <button className="btn btn-ghost btn-sm" type="button" disabled={!field} onClick={() => onAdd(field, aggregate)}>
          <Icon name="plus" size="sm" />
          {t('report.measure.add')}
        </button>
      </span>
    </div>
  )
}

/** Поле ввода под тип колонки: дата уходит в ISO (yyyy-mm-dd), которое сервер и разбирает. */
function inputType(type: ReportField['type'] | undefined): 'date' | 'number' | 'text' {
  return type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'
}

/** Фильтры: колонка, оператор (по типу колонки), значение. */
function FilterWell({
  source,
  definition,
  onAdd,
  onUpdate,
  onRemove,
}: {
  source: ReportSource
  definition: ReportDefinition
  onAdd: (field: ReportField) => void
  onUpdate: (index: number, change: Partial<ReportFilter>) => void
  onRemove: (index: number) => void
}) {
  const { t } = useApp()
  const [field, setField] = useState('')
  return (
    <div className="well" role="group" aria-label={t('report.filters')}>
      <span className="well-label">{t('report.filters')}</span>
      {definition.filters.map((filter, index) => {
        const described = source.filters.find((option) => option.name === filter.field)
        const operators = described ? operatorsFor(described.type) : []
        return (
          <span key={`${filter.field}-${index}`} className="ftoken">
            <span className="k">{t(`column.${filter.field}`)}</span>
            <span className="op">
              <select
                className="wa-pill-select"
                aria-label={t('report.filter.operator')}
                value={filter.operator}
                onChange={(event) => onUpdate(index, withOperator(filter, event.target.value as ReportFilter['operator']))}
              >
                {operators.map((operator) => (
                  <option key={operator} value={operator}>{t(`filter.op.${operator}`)}</option>
                ))}
              </select>
            </span>
            {filter.operator === 'between' ? (
              // Диапазон — два значения: сервер принимает его только списком из двух.
              (['from', 'to'] as const).map((bound, at) => {
                const range = Array.isArray(filter.value) ? filter.value : ['', '']
                return (
                  <input
                    key={bound}
                    className="wa-inline-input"
                    aria-label={t(`report.filter.${bound}`)}
                    type={inputType(described?.type)}
                    value={String(range[at] ?? '')}
                    onChange={(event) => {
                      const next = [range[0] ?? '', range[1] ?? '']
                      next[at] = event.target.value
                      onUpdate(index, { value: next })
                    }}
                  />
                )
              })
            ) : takesValue(filter.operator) && (
              <input
                className="wa-inline-input"
                aria-label={t('report.filter.value')}
                type={inputType(described?.type)}
                value={String(filter.value ?? '')}
                onChange={(event) => onUpdate(index, { value: event.target.value })}
              />
            )}
            <button type="button" aria-label={t('rb.removeFilter', { name: t(`column.${filter.field}`) })} onClick={() => onRemove(index)}>
              <Icon name="close" />
            </button>
          </span>
        )
      })}
      <span className="row" style={{ gap: 4 }}>
        <select className="input input-sm select" aria-label={t('report.filter.field')} value={field} onChange={(e) => setField(e.target.value)} style={{ width: 'auto' }}>
          <option value="">{t('report.filter.choose')}</option>
          {source.filters.map((option) => (
            <option key={option.name} value={option.name}>{t(`column.${option.name}`)}</option>
          ))}
        </select>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          disabled={!field}
          onClick={() => {
            const chosen = source.filters.find((option) => option.name === field)
            if (chosen) onAdd(chosen)
          }}
        >
          <Icon name="filter" size="sm" />
          {t('report.filter.add')}
        </button>
      </span>
    </div>
  )
}

/** Сортировка по выбранным колонкам результата. */
function SortWell({ definition, onChange }: { definition: ReportDefinition; onChange: (sort: ReportDefinition['sort']) => void }) {
  const { t } = useApp()
  const options = [
    ...definition.dimensions.map((field) => ({ value: field, label: t(`column.${field}`) })),
    ...definition.measures.map((m) => ({ value: m.alias, label: `${t(`column.${m.field}`)} · ${t(`report.aggregate.${m.aggregate}`)}` })),
  ]
  const current = definition.sort[0]
  return (
    <div className="well" role="group" aria-label={t('rb.sort')}>
      <span className="well-label">{t('rb.sort')}</span>
      {current ? (
        <span className="fpill">
          {options.find((o) => o.value === current.field)?.label ?? current.field}
          <button
            type="button"
            style={{ width: 'auto', padding: '0 4px', gap: 2, opacity: 1 }}
            aria-label={t('rb.sortDirection')}
            onClick={() => onChange([{ ...current, descending: !current.descending }])}
          >
            <Icon name={current.descending ? 'arrow-down' : 'arrow-up'} size="xs" />
            <span className="t" style={{ textTransform: 'none', fontFamily: 'var(--font-sans)', fontSize: 11 }}>
              {current.descending ? t('rb.desc') : t('rb.asc')}
            </span>
          </button>
          <button type="button" aria-label={t('rb.removeSort')} onClick={() => onChange([])}>
            <Icon name="close" size="xs" />
          </button>
        </span>
      ) : options.length === 0 ? (
        <span className="t-sm muted">{t('rb.sortEmpty')}</span>
      ) : (
        <select
          className="input input-sm select"
          aria-label={t('rb.sort')}
          value=""
          onChange={(event) => event.target.value && onChange([{ field: event.target.value, descending: true }])}
          style={{ width: 'auto' }}
        >
          <option value="">{t('rb.sortChoose')}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      )}
    </div>
  )
}

function PreviewBody({
  definition,
  source,
  preview,
  error,
  busy,
  onAddMeasure,
  onRetry,
}: {
  definition: ReportDefinition | null
  source: ReportSource | null
  preview: ReportResult | null
  error: unknown
  busy: boolean
  onAddMeasure: (field: string) => void
  onRetry: () => void
}) {
  const { t } = useApp()
  if (busy) {
    return (
      <div className="panel-b stack" style={{ gap: 10 }} aria-busy="true">
        {Array.from({ length: 6 }, (_, index) => (
          <span key={index} className="skel" style={{ width: `${50 + ((index * 17) % 40)}%` }} />
        ))}
      </div>
    )
  }
  if (error) {
    if (error instanceof ApiError && (error.code === 'report.forbidden_source' || error.isForbidden)) {
      // Отказ по источнику — запретом, а не пустой таблицей.
      return (
        <StateView
          kind="forbidden"
          title={t('report.forbidden')}
          description={error.message || t('rb.forbiddenSourceText')}
        />
      )
    }
    return (
      <StateView
        kind="error"
        title={t('rb.previewError')}
        description={describe(error, t)}
        actions={
          definition ? (
            <button className="btn btn-secondary btn-sm" type="button" onClick={onRetry}>
              <Icon name="refresh" size="sm" />
              {t('state.retry')}
            </button>
          ) : undefined
        }
        code={error instanceof ApiError ? `${error.status}${error.code ? ` · ${error.code}` : ''}` : undefined}
      />
    )
  }
  if (preview) return <ResultTable result={preview} definition={definition} />
  if (!definition) return <StateView kind="empty" title={t('rb.chooseSource')} description={t('rb.chooseSourceText')} />
  if (definition.measures.length === 0) {
    const first = source?.measures[0]
    return (
      <StateView
        kind="empty"
        title={t('rb.noMeasure')}
        description={t('rb.noMeasureText')}
        actions={
          first ? (
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => onAddMeasure(first.name)}>
              <Icon name="plus" size="sm" />
              {t('rb.addFirstMeasure', { name: t(`column.${first.name}`) })}
            </button>
          ) : undefined
        }
      />
    )
  }
  return <StateView kind="empty" title={t('rb.pressPreview')} />
}

/** Таблица результата: колонки в том порядке, в каком их вернул сервер. */
function ResultTable({ result, definition }: { result: ReportResult; definition: ReportDefinition | null }) {
  const { t, locale } = useApp()
  const header = (column: string) => reportColumnHeader(t, definition, column)
  const isMeasure = (column: string) => Boolean(definition?.measures.some((m) => m.alias === column))
  if (result.rows.length === 0) return <StateView kind="empty" title={t('rb.resultEmpty')} />
  return (
    <>
      <div className="wa-scroll">
        <table className="tbl">
          <thead>
            <tr>
              {result.columns.map((column) => (
                <th key={column} className={isMeasure(column) ? 'num' : undefined}>{header(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, index) => (
              <tr key={index}>
                {result.columns.map((column) => (
                  <td key={column} className={isMeasure(column) ? 'num' : undefined}>
                    {cell(row[column], isMeasure(column), locale)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel-f muted">
        <Icon name="alert" size="sm" />
        <span>{t('rb.previewFooter', { count: result.rows.length })}</span>
      </div>
    </>
  )
}

/** Сохранённые отчёты: запуск, выгрузка, удаление — по правам. */
function SavedReports({
  reports,
  isLoading,
  loadedId,
  mayExport,
  mayDelete,
  onRun,
  onExport,
  onDelete,
}: {
  reports: SavedReport[]
  isLoading: boolean
  loadedId: string | null
  mayExport: boolean
  mayDelete: boolean
  onRun: (report: SavedReport) => void
  onExport: (report: SavedReport) => void
  onDelete: (report: SavedReport) => void
}) {
  const { t } = useApp()
  return (
    <section className="panel wa-saved" aria-labelledby="rb-saved">
      <div className="panel-h">
        <span className="h2" id="rb-saved">{t('report.saved.title')}</span>
        {!isLoading && <span className="count">{reports.length}</span>}
      </div>
      {isLoading ? (
        <div className="panel-b"><span className="skel" style={{ width: '70%' }} /></div>
      ) : reports.length === 0 ? (
        <div className="panel-b secondary">{t('report.saved.empty')}</div>
      ) : (
        reports.map((report) => (
          <div key={report.id} className={report.id === loadedId ? 'list-item is-selected' : 'list-item'}>
            <div className="stack" style={{ flex: 1, gap: 0, minWidth: 0 }}>
              <span className="ellipsis">{report.name}</span>
              <span className="t-xs muted">{t(`report.category.${report.category}`)}</span>
            </div>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => onRun(report)}>
              {t('report.run')}
            </button>
            {mayExport && (
              <button className="btn btn-ghost btn-sm btn-icon" type="button" aria-label={t('rb.exportXlsxOf', { name: report.name })} title={t('rb.exportXlsx')} onClick={() => onExport(report)}>
                <Icon name="download" size="sm" />
              </button>
            )}
            {mayDelete && !report.isSystem && (
              <MoreMenu
                compact
                label={t('rb.savedMenu', { name: report.name })}
                items={[{ id: 'delete', label: t('report.delete'), icon: 'trash', danger: true, onSelect: () => onDelete(report) }]}
              />
            )}
          </div>
        ))
      )}
    </section>
  )
}

function cell(value: unknown, numeric: boolean, locale: 'ru' | 'en'): string {
  if (value == null || value === undefined || value === '') return '—'
  if (numeric) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return formatNumber(parsed, locale)
  }
  return String(value)
}

/**
 * Ошибка словами. Отказ по источнику — своим текстом: он означает, что данные закрыты, а не
 * что сервер сломался, и путать эти сообщения значит отправлять в поддержку с работающим
 * запретом.
 */
function describe(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'report.forbidden_source' || error.isForbidden) return t('report.forbidden')
    return error.message
  }
  return t('app.error')
}

/** Runs shown under a loaded report: enough to answer «did the morning run fail». */
const RUNS_SHOWN = 10

/**
 * Run journal of the opened report (#94, `GET /analytics/reports/{id}/runs`).
 *
 * A failed run is named with the server's message, not reduced to a red mark: «the report
 * did not come» and «the report came empty» are different answers, and the journal is where
 * the difference is read. Who ran it is left out on purpose — the journal carries an account
 * id, not a person, and a bare id here would be a key on the screen.
 */
function RunHistory({ reportId }: { reportId: string }) {
  const { t, locale } = useApp()
  const runs = useQuery({
    queryKey: ['report-runs', reportId],
    queryFn: ({ signal }) => fetchRuns(reportId, signal),
    retry: false,
  })
  const list: ReportRun[] = (runs.data ?? []).slice(0, RUNS_SHOWN)
  const when = (at: string | null) => (at ? new Date(at).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU') : '—')
  return (
    <section className="panel" aria-labelledby="rb-runs">
      <div className="panel-h">
        <span className="h2" id="rb-runs">{t('rb.runs.title')}</span>
      </div>
      {runs.isLoading ? (
        <div className="panel-b"><span className="skel" style={{ width: '60%' }} /></div>
      ) : runs.error ? (
        <div className="panel-b secondary" role="alert">{describe(runs.error, t)}</div>
      ) : list.length === 0 ? (
        <div className="panel-b secondary">{t('rb.runs.empty')}</div>
      ) : (
        <ul className="panel-b stack" style={{ gap: 6, listStyle: 'none', margin: 0 }} aria-label={t('rb.runs.title')}>
          {list.map((run) => (
            <li key={run.id} className="stack" style={{ gap: 0 }}>
              <span className="t-sm">
                {when(run.startedAt)} · {t(`rb.runs.status.${run.status}`)}
                {run.rowCount != null ? ` · ${t('rb.runs.rows', { count: run.rowCount })}` : ''}
              </span>
              {run.errorMessage && <span className="t-xs" style={{ color: 'var(--danger-text)' }}>{run.errorMessage}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
